"use client";

import { Users } from "lucide-react";
import { DemoPreviewCard } from "@/components/landing/demo-preview";
import { WaitlistModal } from "@/components/waitlist-modal";
import { GUIDED_DEMO_SAMPLE_PREVIEW } from "@/src/features/demo-contracts/sample-data";

export function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-primary px-6 py-24 lg:px-8 lg:py-32">
      {/* Evidence-wall atmosphere — restrained, no decorative blur blobs */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        {/* 1px rule at top */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/8 to-transparent" />
        {/* Faint grid overlay */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.02) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-16">
          {/* Left: Hero text */}
          <div className="text-center lg:text-left">
            {/* Launch badge — monospace label, tight */}
            <div className="inline-flex items-center gap-2 border border-white/10 bg-white/[0.04] px-3 py-1">
              <Users className="size-3 text-accent" aria-hidden="true" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-white/65">
                For founders tired of building in the dark
              </span>
            </div>

            <h1 className="mx-auto mt-6 max-w-3xl font-serif text-4xl font-bold leading-[1.1] tracking-tight text-primary-foreground sm:text-5xl lg:mx-0 lg:text-6xl xl:text-7xl">
              <span className="text-balance">
                You can ship for months and still hear{" "}
                <span className="text-accent">
                  &quot;I don&apos;t need this.&quot;
                </span>
              </span>
            </h1>

            {/* Supporting copy — tighter line-height, structured */}
            <p className="mx-auto mt-5 max-w-xl font-mono text-sm leading-[1.7] text-white/65 sm:text-base lg:mx-0 lg:text-lg">
              Most startup ideas fail before launch because validation happens
              too late. See the pains founders already complain about across
              Reddit and build from real demand.
            </p>

            {/* Metadata accent line */}
            <div className="mx-auto mt-6 max-w-sm flex items-center gap-3 lg:mx-0 lg:justify-start">
              <div className="h-px flex-1 bg-white/10" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/45">
                Evidence before execution
              </span>
            </div>

            <div className="mx-auto mt-8 flex w-full max-w-sm justify-center lg:mx-0 lg:justify-start">
              <WaitlistModal
                triggerText="Apply for founding access"
                analyticsSource="hero_section"
              />
            </div>

            <p className="mt-3 font-mono text-[11px] text-white/55 lg:text-left">
              Reserved for founders who want evidence before their next build
              sprint.
            </p>
          </div>

          {/* Right: Product preview card — evidence surface */}
          <div className="mx-auto w-full max-w-lg lg:mx-0 lg:max-w-none">
            <DemoPreviewCard preview={GUIDED_DEMO_SAMPLE_PREVIEW} />
          </div>
        </div>
      </div>
    </section>
  );
}
